import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Package, EnvelopeSimple, Phone, MapPin, Globe, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { AdminHeader, Field, inputCls, PrimaryButton, GhostButton } from "@/components/admin/parts";
import { adminApi, getErrorMessage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const BLANK = {
  name: "", phone: "", email: "", website: "",
  address: "", suburb: "", state: "", postcode: "",
  manual_coords: false, lat: "", lng: "",
};

export default function AdminSuppliers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    adminApi.get("/admin/suppliers")
      .then(({ data }) => setRows(data))
      .catch((e) => toast.error(getErrorMessage(e, "Couldn't load suppliers. Check your connection and retry.")))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ ...BLANK }); setOpen(true); };
  const openEdit = (r) => {
    setEditing({ ...BLANK, ...r, lat: r.lat ?? "", lng: r.lng ?? "", manual_coords: !!r.manual_coords });
    setOpen(true);
  };

  const save = async () => {
    if (!editing.name.trim()) { toast.error("Supplier name is required"); return; }
    setSaving(true);
    const payload = {
      ...editing,
      lat: editing.manual_coords && editing.lat !== "" ? Number(editing.lat) : null,
      lng: editing.manual_coords && editing.lng !== "" ? Number(editing.lng) : null,
    };
    try {
      if (editing.id) await adminApi.put(`/admin/suppliers/${editing.id}`, payload);
      else await adminApi.post("/admin/suppliers", payload);
      toast.success("Saved — location resolved on the map");
      setOpen(false); load();
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setSaving(false); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete "${r.name}"?`)) return;
    await adminApi.delete(`/admin/suppliers/${r.id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div>
      <AdminHeader
        title="Suppliers"
        subtitle="Component brands (Projecta, Renogy, Dometic…) shown as blue pins on the Repair Network map so customers can call them directly. Add a suburb/state/postcode to place them on the map."
        actions={<PrimaryButton onClick={openNew} data-testid="supplier-new"><Plus size={16} /> Add Supplier</PrimaryButton>}
      />
      <div className="p-8">
        {loading ? (
          <div className="text-neutral-600">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="text-neutral-600 border border-dashed border-neutral-800 rounded-xl py-16 text-center flex flex-col items-center gap-3">
            <Package size={36} className="text-neutral-700" weight="duotone" /> No suppliers yet.
          </div>
        ) : (
          <div className="bg-[#0F0F0F] border border-neutral-800 rounded-xl overflow-hidden">
            <table className="w-full text-sm" data-testid="suppliers-table">
              <thead>
                <tr className="text-left text-[0.65rem] uppercase tracking-wider text-neutral-500 border-b border-neutral-800">
                  <th className="px-5 py-3 font-semibold">Supplier</th>
                  <th className="px-5 py-3 font-semibold">Contact</th>
                  <th className="px-5 py-3 font-semibold">Location</th>
                  <th className="px-5 py-3 font-semibold">Map</th>
                  <th className="px-5 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {rows.map((r) => (
                  <tr key={r.id} data-testid="supplier-row" className="hover:bg-white/[0.02]">
                    <td className="px-5 py-4">
                      <div className="font-heading font-semibold text-white flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-sky-400" /> {r.name}
                      </div>
                      {r.website && <div className="text-xs text-neutral-500 mt-0.5">{r.website}</div>}
                    </td>
                    <td className="px-5 py-4 text-neutral-300">
                      {r.phone && <div className="flex items-center gap-2"><Phone size={14} className="text-sky-400" /> {r.phone}</div>}
                      {r.email && <div className="flex items-center gap-2 mt-1 text-neutral-400"><EnvelopeSimple size={14} className="text-sky-400" /> {r.email}</div>}
                    </td>
                    <td className="px-5 py-4 text-neutral-300">
                      <div className="flex items-start gap-2">
                        <MapPin size={14} className="text-neutral-500 mt-0.5" />
                        <span>{[r.address, r.suburb, r.state, r.postcode].filter(Boolean).join(", ") || "—"}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      {r.lat != null && r.lng != null ? (
                        <span className="inline-flex items-center gap-1.5 text-[0.65rem] font-semibold text-sky-400">
                          <CheckCircle size={14} weight="fill" /> Located
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-[0.65rem] font-semibold text-amber-400">
                          <WarningCircle size={14} weight="fill" /> No map pin
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => openEdit(r)} data-testid="supplier-edit" className="p-2 rounded-lg text-neutral-400 hover:text-white hover:bg-white/[0.05]"><PencilSimple size={16} /></button>
                        <button onClick={() => del(r)} data-testid="supplier-delete" className="p-2 rounded-lg text-neutral-400 hover:text-red-400 hover:bg-red-500/10"><Trash size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F0F0F] border-neutral-800 text-neutral-200 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-white">{editing?.id ? "Edit" : "Add"} Supplier</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <Field label="Supplier / Brand Name"><input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={inputCls} data-testid="supplier-name" placeholder="Projecta" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Phone"><input value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} className={inputCls} data-testid="supplier-phone" placeholder="1300 000 000" /></Field>
                <Field label="Email (optional)"><input value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className={inputCls} data-testid="supplier-email" placeholder="support@brand.com.au" /></Field>
              </div>
              <Field label="Website (optional)"><input value={editing.website} onChange={(e) => setEditing({ ...editing, website: e.target.value })} className={inputCls} data-testid="supplier-website" placeholder="https://brand.com.au" /></Field>
              <Field label="Street Address (optional)"><input value={editing.address} onChange={(e) => setEditing({ ...editing, address: e.target.value })} className={inputCls} data-testid="supplier-address" placeholder="Leave blank to just use suburb/state" /></Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Suburb"><input value={editing.suburb} onChange={(e) => setEditing({ ...editing, suburb: e.target.value })} className={inputCls} data-testid="supplier-suburb" placeholder="Kilsyth" /></Field>
                <Field label="State"><input value={editing.state} onChange={(e) => setEditing({ ...editing, state: e.target.value })} className={inputCls} data-testid="supplier-state" placeholder="VIC" /></Field>
                <Field label="Postcode"><input value={editing.postcode} onChange={(e) => setEditing({ ...editing, postcode: e.target.value })} className={inputCls} data-testid="supplier-postcode" placeholder="3137" /></Field>
              </div>

              <label className="flex items-center gap-2 cursor-pointer pt-1">
                <input type="checkbox" checked={editing.manual_coords} onChange={(e) => setEditing({ ...editing, manual_coords: e.target.checked })} className="accent-sky-500 w-4 h-4" data-testid="supplier-manual-coords" />
                <span className="text-sm text-neutral-300">Set map location manually (otherwise it's found from the location)</span>
              </label>
              {editing.manual_coords && (
                <div className="grid grid-cols-2 gap-3 border-l-2 border-sky-500/40 pl-4">
                  <Field label="Latitude"><input value={editing.lat} onChange={(e) => setEditing({ ...editing, lat: e.target.value })} className={inputCls} data-testid="supplier-lat" placeholder="-37.80" /></Field>
                  <Field label="Longitude"><input value={editing.lng} onChange={(e) => setEditing({ ...editing, lng: e.target.value })} className={inputCls} data-testid="supplier-lng" placeholder="145.31" /></Field>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <GhostButton onClick={() => setOpen(false)}>Cancel</GhostButton>
                <PrimaryButton onClick={save} disabled={saving} data-testid="supplier-save">{saving ? "Saving…" : "Save"}</PrimaryButton>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
